import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { Text, Button, Box, Tooltip, createStyles, Progress, Popover, Indicator, Grid, ScrollArea, Drawer } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import Swal from "sweetalert2";

import isSpaceThemed from "../../utils/IsSpaceThemed";
import useSidebarStyles from "../../utils/SidebarStyles";
import defaultSpaceColors from "../../collections/defaultSpaceColors";
import useNotificationsStyle from "./style/notifications.js";

import tinycolor from "tinycolor2";
import AWS from "aws-sdk";
import Broadcaster from "../../helpers/Broadcaster";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";

import MediaFileModel from "../../models/MediaFile";

const useStyles = createStyles((theme, { colors }) => ({
  primaryButtonStyle: {
    backgroundColor: isSpaceThemed(colors)
      ? colors.primary_button_background_color
      : theme.colors.gray[2],
    color: isSpaceThemed(colors) ? colors.primary_button_text_color : "#000000",
    "&:hover": {
      backgroundColor: isSpaceThemed(colors)
        ? tinycolor(colors.primary_button_background_color).darken(4).toString()
        : theme.colors.gray[1],
    },
  },
}));

const S3_BUCKET = "sutra-froala";
const REGION = "us-west-1";

const STATUS = {
  UPLOADING: "uploading",
  ERROR: "error",
  PROCESSING: "processing",
  COMPLETED: "completed"
};

export default function Uploader(props) {
  const { t } = useTranslation();
  const { notificationsStyle } = useNotificationsStyle();

  const mainActions = useSelector((state) => state.mainActions);

  const { settingsView, registrationView } = useSidebarStyles();
  let colors = useSelector((state) => state.colors);
  colors = !settingsView && !registrationView ? colors : defaultSpaceColors;
  const { classes } = useStyles({ colors });

  const [opened, handlers] = useDisclosure(false);
  const [count, setCount] = useState(0);
  const [uploads, setUploads] = useState([]);

  const compId = useRef();
  const editorRef = useRef(null);
  const uploadTimeRef = useRef({});

  useEffect(() => {
    compId.current = uuidv4();

    AWS.config.update({
      accessKeyId: window.$RAILS_ENV.S3_ACCESS_KEY,
      secretAccessKey: window.$RAILS_ENV.S3_SECRET_KEY,
    });

    AWS.config.httpOptions.timeout = 0;

    Broadcaster.receive("new_uploadFile", document.body, (event, data) => {
      if (!document.getElementById(compId.current)) {
        return;
      }

      if (data.status === "pending") {
        if (data.editor) {
          editorRef.current = data.editor;
        }
        
        processUpload(data);
        setTimeout(() => handlers.open(), 500);
      } else if (data.status === "used") {
        updateMediaNode(data);
        markUploadAsUsed(data);
      }
    });

    // Add beforeunload event listener
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      Broadcaster.unRegisterElement("new_uploadFile", document.body);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    loadCurrentUploads();
    cleanupOldUploads();
  }, [mainActions]);

  // Show SweetAlert confirmation when user is trying to leave the page
  const handleBeforeUnload = (event) => {
    const hasActiveUploads = window.$currentUploads && window.$currentUploads.some(
      upload => upload.status === STATUS.UPLOADING
    );

    if (hasActiveUploads) {
      const message = "Are you sure you want to leave this page? Your uploads might not be saved.";
      event.preventDefault();
      event.returnValue = message;
      
      // Note: This won't actually show SweetAlert because browser doesn't allow it on beforeunload
      // This is just for the browser's default confirmation dialog
      return message;
    }
  };
  
  const updateMediaNode = (data) => {
    console.log("USED", data);
    MediaFileModel.onUpdateVideoNode(
      {
        space_slug: data.space_slug,
        video_id: data.id,
        new_src: data.new_src,
        new_status: data.new_status
      },
      () => {
        console.log("SUCCESS")
      },
      (err) => {
        console.log("ERROR", err)
      }
    );
  }

  // Function to show alerts with SweetAlert
  const showAlert = (config) => {
    return Swal.fire({
      icon: config.icon || "info",
      title: config.title || "",
      text: config.text || "",
      timer: config.timer,
      timerProgressBar: !!config.timer,
      ...config
    });
  };

  const loadCurrentUploads = () => {
    const currentUploads = localStorage.getItem("currentUploads");
    if (currentUploads) {
      window.$currentUploads = JSON.parse(currentUploads);
      refreshUploadsUI();
    } else {
      window.$currentUploads = [];
      localStorage.setItem("currentUploads", "[]");
      refreshUploadsUI();
    }
  };

  const cleanupOldUploads = () => {
    const storedTimestamp = localStorage.getItem("lastUploadedAt");

    if (storedTimestamp) {
      const storedDate = new Date(parseInt(storedTimestamp, 10));
      const currentDate = new Date();
      const timeDifference = currentDate - storedDate;
      const oneDayThreshold = 24 * 60 * 60 * 1000;

      if (timeDifference >= oneDayThreshold) {
        localStorage.setItem("currentUploads", "[]");
        window.$currentUploads = [];
        refreshUploadsUI();
      }
    } else {
      localStorage.setItem("currentUploads", "[]");
      window.$currentUploads = [];
      refreshUploadsUI();
    }
  };

  const markUploadAsUsed = (data) => {
    if (!window.$currentUploads) return;
    
    const index = window.$currentUploads.findIndex(upload => upload.id === data.id);
    if (index !== -1) {
      window.$currentUploads.splice(index, 1);
      localStorage.setItem("currentUploads", JSON.stringify(window.$currentUploads));
      storeLastUploadTime();
      refreshUploadsUI();
    }
  };

  const storeLastUploadTime = () => {
    localStorage.setItem("lastUploadedAt", new Date().getTime().toString());
  };

  const refreshUploadsUI = () => {
    if (window.$currentUploads) {
      setCount(window.$currentUploads.length);
      setUploads([...window.$currentUploads]);
    }
  };

  const processUpload = (data) => {
    if (!window.$currentUploads) {
      window.$currentUploads = [];
    }

    const existingUploadIndex = window.$currentUploads.findIndex(
      upload => upload.id === data.id
    );
    
    if (existingUploadIndex === -1) {
      const newUpload = {
        id: data.id,
        progress: 0,
        status: STATUS.UPLOADING,
        file: data.file,
        space: data.space,
        extension: data.extension,
        filename: data.file.name,
        type: data.type || "image",
        url: data?.url,
        urlOutput: data?.url,
        space_slug: data?.space_slug,
        createdAt: Date.now()
      };

      // Add to the beginning of the array
      window.$currentUploads.unshift(newUpload);
      localStorage.setItem("currentUploads", JSON.stringify(window.$currentUploads));
      storeLastUploadTime();
      
      uploadToS3(newUpload);
    }

    refreshUploadsUI();
  };

  const uploadToS3 = (upload) => {
    const s3 = new AWS.S3({
      params: { Bucket: S3_BUCKET },
      region: REGION,
      httpOptions: {
        timeout: 300000
      }
    });

    const key = `uploads/${upload.id}`;
    
    upload.status = STATUS.UPLOADING;
    localStorage.setItem("currentUploads", JSON.stringify(window.$currentUploads));
    refreshUploadsUI();
    
    const fileParams = {
      ACL: "public-read",
      Body: upload.file,
      Bucket: S3_BUCKET,
      Key: key,
      ContentType: upload.file.type
    };
    
    if (upload.file.size > 300 * 1024 * 1024) {
      fileParams.partSize = 5 * 1024 * 1024;
      fileParams.queueSize = 4;
    } else if (upload.file.size > 100 * 1024 * 1024) {
      fileParams.partSize = 3 * 1024 * 1024;
      fileParams.queueSize = 3;
    } else if (upload.file.size > 20 * 1024 * 1024) {
      fileParams.partSize = 2 * 1024 * 1024;
      fileParams.queueSize = 2;
    }
  
    const folderParams = {
      ACL: "public-read",
      Body: "",
      Bucket: S3_BUCKET,
      Key: key.split("/")[0] + "/",
    };

    s3.putObject(folderParams, (folderErr, folderData) => {
      if (folderErr) {
        console.log("Error creating folder:", folderErr);
      }
      
      const uploadTask = s3.upload(fileParams);
      
      upload.cancelUpload = () => {
        try {
          uploadTask.abort();
          upload.status = STATUS.ERROR;
          upload.error = "Upload was cancelled";
          localStorage.setItem("currentUploads", JSON.stringify(window.$currentUploads));
          refreshUploadsUI();
          
        } catch (e) {
          console.log("Error cancelling upload:", e);
        }
      };
      
      let lastProgress = 0;
      let uploadStartTime = Date.now();
      uploadTimeRef.current[upload.id] = { startTime: uploadStartTime };
      
      uploadTask.on('httpUploadProgress', (evt) => {
        const newProgress = Math.round((evt.loaded / evt.total) * 100);
        
        if (newProgress - lastProgress >= 2 || newProgress === 100) {
          lastProgress = newProgress;
          upload.progress = newProgress;
          
          if (upload.file.size > 20 * 1024 * 1024 && newProgress > 0 && newProgress < 100) {
            const elapsedTime = Date.now() - uploadStartTime;
            const estimatedTotalTime = (elapsedTime / newProgress) * 100;
            const remainingTime = estimatedTotalTime - elapsedTime;
            
            if (remainingTime > 5000) {
              const minutes = Math.floor(remainingTime / 60000);
              const seconds = Math.floor((remainingTime % 60000) / 1000);
              upload.estimatedTime = minutes > 0 ? `Time left: ${minutes} min ${seconds} sec` : `Time left: ${seconds} sec`;
              uploadTimeRef.current[upload.id].estimatedTime = upload.estimatedTime;
            }
          }

          localStorage.setItem("currentUploads", JSON.stringify(window.$currentUploads));
          refreshUploadsUI();
        }
      });
      
      uploadTask.send((err, data) => {
        handlers.close();
        if (err) {
          console.log("Upload error:", err);
          
          let errorMessage = "Error uploading file";
          if (err.code === "RequestTimeout") {
            errorMessage = "Request timeout. Check your connection.";
          } else if (err.code === "NetworkingError") {
            errorMessage = "Network error. Check your connection.";
          }
          
          upload.status = STATUS.ERROR;
          upload.error = errorMessage;
          updateNode(upload);
          localStorage.setItem("currentUploads", JSON.stringify(window.$currentUploads));
          refreshUploadsUI();
          
          // Show error alert
          showAlert({
            icon: "error",
            title: "Upload Failed",
            text: errorMessage
          });
        } else {
          upload.url = data.Location;
          upload.progress = 100;
          
          if (upload.type === "video") {
            // For video, set processing status and update node
            upload.status = STATUS.PROCESSING;

            updateMediaNode({
              space_slug: upload.space_slug,
              video_id: upload.id,
              new_src: upload.url,
              new_status: STATUS.PROCESSING
            });

            updateNode(upload);

            // Show alert for video processing
            showAlert({
              icon: "success",
              title: "Video Uploaded Successfully",
              text: "Your video is now processing and will be available for viewing shortly.",
              timer: 5000
            });

            // Mark upload as used to remove from UI
            setTimeout(() => {
              markUploadAsUsed({ id: upload.id });
            }, 500);
          } else {
            // For non-video files, just update node
            updateNode(upload);
            
            // Show simple success message
            showAlert({
              icon: "success",
              title: "Upload Successful",
              timer: 3000
            });
            
            // Mark upload as used to remove from UI
            setTimeout(() => {
              markUploadAsUsed({ id: upload.id });
            }, 500);
          }
        }
      });
    });
  };

  const updateNode = (upload) => {
    const element = document.getElementById(upload.id);
    if (element && editorRef.current) {
      try {
        const rect = element.getBoundingClientRect();
        const auxPos = editorRef.current.view.posAtCoords({
          left: rect.left,
          top: rect.top,
        });
        if (auxPos) {
          const theNode = editorRef.current.view.state.doc.nodeAt(auxPos.inside);
          if (
            theNode.attrs.uploader &&
            theNode.attrs.uploader.id == upload.id
          ) {
            const { tr } = editorRef.current.state;
            tr.setNodeMarkup(auxPos.inside, undefined, {
              ...theNode.attrs,
              src: upload.url,
              uploader: upload
            });
            editorRef.current.view.dispatch(tr);
          }
        }
      } catch (error) {
        console.log("Error updating node:", error);
      }
    }
  };

  const shouldHide = () => {
    return !window.$currentUploads || window.$currentUploads.length === 0;
  };

  const getStatusMessage = (upload) => {
    if (upload.status === STATUS.ERROR) {
      return upload.error || "Upload Error";
    }
    if (upload.status === STATUS.UPLOADING) {
      return uploadTimeRef.current[upload.id]?.estimatedTime || "Uploading...";
    }
    if (upload.status === STATUS.PROCESSING) {
      return "Video is processing. It will be available soon...";
    }
    if (upload.status === STATUS.COMPLETED) {
      return "Upload Completed";
    }
    return upload.status || "Waiting...";
  };

  const RenderTriggerButton = () => {
    return (
      <Indicator
        size={count > 0 ? 20 : 0}
        label={count > 0 ? count : ""}
        offset={4}
        color="sutrapink"
        onClick={() => handlers.toggle()}
        sx={(theme) => ({
          cursor: "pointer",
          "&:hover button": {
            backgroundColor: isSpaceThemed(colors)
              ? tinycolor(colors.primary_button_background_color)
                  .darken(4)
                  .toString()
              : theme.colors.gray[1],
          },
        })}
      >
        <Tooltip
          disabled={window.$isTouchDevice && window.$isTouchDevice()}
          label="Uploads"
          withArrow
        >
          <Button
            variant="light"
            color="dark"
            radius="xl"
            onClick={() => handlers.toggle()}
            className={classes.primaryButtonStyle}
          >
            <FontAwesomeIcon icon={solid("cloud-arrow-up")} />
          </Button>
        </Tooltip>
      </Indicator>
    );
  };

  const RenderInsides = () => {
    return (
      <Box>
        <ScrollArea style={{ height: props.component === "drawer" ? "calc(100vh - 100px)" : "calc(100vh - 150px)" }}>
          {uploads.map((upload) => (
            <Box key={upload.id} sx={(theme) => notificationsStyle.notificationItem}>
              <Grid grow>
                <Grid.Col span={10}>
                  <Text color="dimmed" size="sm">
                    {upload.space.name}
                  </Text>
                  <Box sx={{ overflow: "hidden", maxWidth: "250px" }}>
                    <Text
                      color="black"
                      weight="600"
                      pb={2}
                      sx={{ lineHeight: 1.2 }}
                      lineClamp={1}
                    >
                      {upload.filename || upload.file.name}
                    </Text>
                  </Box>
                  <Progress
                    color={
                      upload.status === STATUS.ERROR 
                        ? "red"
                        : upload.status === STATUS.PROCESSING
                        ? "orange"
                        : "blue"
                    }
                    size="xl"
                    mb={10}
                    value={upload.progress}
                    animate={upload.status === STATUS.UPLOADING || upload.status === STATUS.PROCESSING}
                    striped={upload.status === STATUS.PROCESSING}
                  />
                  <Text
                    size="xs"
                    sx={(theme) => ({
                      color: upload.status === STATUS.ERROR 
                        ? theme.colors.red[6] 
                        : theme.colors.sutrapink[4],
                    })}
                  >
                    {getStatusMessage(upload)}
                  </Text>
                </Grid.Col>
              </Grid>
            </Box>
          ))}
        </ScrollArea>
      </Box>
    );
  };

  return (
    <>
      {props.component === "drawer" ? (
        <Drawer
          opened={props.opened}
          onClose={() => props.setOpened(false)}
          title=""
          padding={0}
          size="lg"
        >
          {RenderInsides()}
        </Drawer>
      ) : (
        <Popover
          id={compId.current}
          style={{ display: !shouldHide() ? "block" : "none" }}
          opened={opened}
          onClose={handlers.close}
          width={300}
          position="bottom"
          placement="end"
          spacing={0}
          shadow="lg"
          target={RenderTriggerButton()}
        >
          {RenderInsides()}
        </Popover>
      )}
    </>
  );
}